/**
 * Confirmación de la marcación en Staff (004 · US1 · T030; ampliada en 006 · T028 · US1;
 * spec US1-2/US1-9, FR-032, FR-018; design-system §8.1 · "Pantalla de éxito"; contracts/staff-ui.md
 * §7).
 *
 * Fondo Crema, círculo Éxito 50 de 96 px con el check, "Entrada registrada" (Nunito 700 22 px),
 * la **hora en la zona horaria de la sede** (36 px tabular) y el nombre de la sede, más una frase
 * de cierre. La vibración la dispara el hook al recibir la marca (`use-marking.ts`).
 *
 * **Regreso automático a Inicio** (T028): a los `SUCCESS_AUTO_RETURN_MS` (2,5 s) se llama a
 * `onExit`, o antes si se toca cualquier parte de la pantalla — el botón "Ir al inicio" que pasa
 * `marking-screen.tsx` en `footer` también lo hace, y sigue siendo el único control accesible por
 * teclado/lector (§7). El temporizador se cancela al desmontar. La invalidación del estado y el
 * historial (R-16) ya ocurrió al entrar en `SUCCESS` (`use-marking.ts`), así que Inicio llega
 * refrescado.
 *
 * **Nada de identificadores** (FR-032): ni el id de la marca, ni el del challenge, ni conteos de
 * frames ni nonces. Cuando la marca viene `FLAGGED` se dice en lenguaje llano que el administrador
 * revisará un detalle, sin nombrar el flag.
 *
 * `data-testid`: `marking-success`, `marking-success-title`, `marking-success-time`,
 * `marking-success-site`.
 */
import { useEffect } from 'react';

import type { AttendanceMarkConfirmation } from '@fermin/contracts';

import { Screen } from '../../components/ui/screen';
import { formatTime, t } from '../../i18n';

import type { ReactNode } from 'react';

/** FR-018/contracts/staff-ui.md §7: regreso automático a Inicio a los 2,5 s. */
export const SUCCESS_AUTO_RETURN_MS = 2_500;

export interface SuccessScreenProps {
  mark: AttendanceMarkConfirmation;
  /**
   * A los `SUCCESS_AUTO_RETURN_MS` o antes, si se toca la pantalla. Opcional (por defecto no hace
   * nada) para no exigirlo en pruebas que solo verifican el contenido de la confirmación; en
   * producción `marking-screen.tsx` siempre lo pasa.
   */
  onExit?: () => void;
  /** Acción del pie de pantalla ("Ir al inicio"). */
  footer?: ReactNode;
}

export function SuccessScreen({ mark, onExit = () => undefined, footer }: SuccessScreenProps) {
  const isCheckIn = mark.type === 'CHECK_IN';
  const title = isCheckIn ? t('attendance.success.checkIn') : t('attendance.success.checkOut');
  const closing = isCheckIn ? t('attendance.success.closingIn') : t('attendance.success.closingOut');
  const time = formatTime(mark.server_timestamp, mark.site.timezone);

  useEffect(() => {
    const timer = window.setTimeout(onExit, SUCCESS_AUTO_RETURN_MS);
    return () => window.clearTimeout(timer);
  }, [onExit]);

  return (
    // El clic en cualquier parte adelanta el regreso (§7); el botón del `footer` es el único
    // control con el que un lector de pantalla o el teclado pueden hacer lo mismo.
    <div data-testid="marking-success" onClick={onExit}>
      <Screen align="center" {...(footer ? { footer } : {})}>
        <div className="flex flex-col items-center gap-fm-4 text-center">
          <span
            aria-hidden="true"
            className="flex h-24 w-24 items-center justify-center rounded-fm-pill bg-fm-success-soft"
          >
            <svg viewBox="0 0 48 48" className="h-12 w-12">
              <path
                d="M12 25 L21 34 L37 16"
                fill="none"
                strokeWidth={5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="stroke-fm-success-text"
              />
            </svg>
          </span>

          <h1
            data-testid="marking-success-title"
            className="font-display text-fm-xl font-bold text-fm-text"
          >
            {title}
          </h1>

          <p
            data-testid="marking-success-time"
            data-time={time}
            className="fm-tabular font-display text-fm-3xl font-extrabold text-fm-text"
          >
            {time}
          </p>

          <p data-testid="marking-success-site" className="text-fm-base text-fm-text-secondary">
            {mark.site.name}
          </p>

          {mark.status === 'FLAGGED' ? (
            <p data-testid="marking-success-flagged" className="text-fm-base text-fm-text-secondary">
              {t('attendance.success.flagged')}
            </p>
          ) : (
            <p className="text-fm-base text-fm-text-secondary">{closing}</p>
          )}
        </div>
      </Screen>
    </div>
  );
}
